<template>
    <!--
      Stays as wide as the visible area, so `100cqw` below can name that width. The wrapper inside cannot:
      it spans the whole timeline, and a percentage width resolves against the containing block.
    -->
    <div class="@container">
        <!--
          `w-max` so this spans the whole timeline: it backs the full width instead of ending at the viewport
          edge once the page is scrolled sideways, and it gives the toolbar a containing block wide enough to
          stay pinned — `sticky` is clamped to its containing block, so a viewport-wide wrapper would slide
          the toolbar along with it. `min-w-full` to still fill the width without a timeline.
        -->
        <div class="-mx-4 -mb-4 p-4 bg-gray-50 w-max min-w-full">
            <!--
              Pinned so scrolling the timeline sideways leaves the controls be, and `100cqw` — the visible
              width, measured outside this wrapper — so they sit at the edges of what is on screen. Pinning
              also makes this a stacking context, so the dropdowns inside cannot rise above the timeline on
              their own: `z-30` lifts the whole toolbar over the timeline's panes instead.
            -->
            <div class="flex flex-row justify-between items-end sticky left-0 z-30 w-[100cqw]">
                <div v-if="!timelineLoading" class="flex flex-row items-end gap-4">
                    <TimelineIntervalChanger
                        v-if="startDateTime"
                        :current-quarter="startDateTime.quarter as QuarterNumbers"
                        :current-year="startDateTime.year"
                        :minimal-year="MINIMAL_YEAR"
                        :maximal-year="MAXIMAL_YEAR"
                        :warning-no-allocations="warningNoAllocations"
                        :is-time-span-modified="isTimeSpanModified"
                        @change-quarter="onChangeQuarter"
                        @change-year="onChangeYear"
                        @click-previous-quarter="onClickPreviousQuarter"
                        @click-next-quarter="onClickNextQuarter"
                    />
                </div>

                <VariantSelectionDropdown
                    v-if="!timelineLoading && mappedCurrentVariant"
                    :drop-down-status="dropDownStatus"
                    :variants-ref="variantsRef"
                    :visible-variants="visibleVariantIds"
                    :default-active-variants="defaultActiveVariants"
                    :selection-panel-variants="selectionPanelVariants"
                    :highlighted-variant="highlightedVariant"
                    :highlighted-variant-id="highlightedVariantId"
                    :mapped-current-variant="mappedCurrentVariant"
                    :allocation-type="allocationType"
                    :object-id="externalEventId"
                    :new-variant-index="newVariantIndex"
                    :category-color="categoryColor"
                    :on-change-visibility="updateVisibility"
                    :set-highlighted-variant-id="setHighlightedVariantIdById"
                    :on-delete-filter="onDeleteFilter"
                    :on-create-new-variant="onCreateNewVariant"
                    :on-copy-allocations="onCopyAllocations"
                />
            </div>

            <Message v-if="timelineLoading" status="info">
                {{ $t('loading-timeline-data') }}
            </Message>

            <Message
                v-if="!timelineLoading && timelineError"
                status="error"
                :headline="$t('error-fetching-timeline-data')"
            >
                {{ timelineError }}
            </Message>

            <Timeline
                v-if="!timelineLoading && startDateTime && endDateTime && editableVariant"
                class="mt-4"
                scroll-container="Page"
                :header-name="timelineHeaderName"
                :header-link-target="timelineHeaderLinkTarget"
                :start-date-time="startDateTime"
                :end-date-time="endDateTime"
                :previous-month-button="previousMonthButton"
                :next-month-button="nextMonthButton"
                :conflicts-by-day="conflictsByDay"
                :site-conflicts-count="siteConflictsCount"
                :update-allocation="updateAllocation"
                @delete-allocation="handleDeleteAllocation"
                @expand-to-previous-month="onExpandToPreviousMonth"
                @expand-to-next-month="onExpandToNextMonth"
            >
                <template #first-column-rows>
                    <ReferenceAllocationFirstColumnCell v-bind="referenceAllocationFirstColumnCell" />
                    <ExternalEventFirstColumnCell v-for="row in firstColumnCells" v-bind="row" :key="row.rowId">
                        <div class="flex flex-row justify-end">
                            <ExternalEventsTriState
                                :status="row.status"
                                :color="categoryColor"
                                @change-status="(status) => handleStatusChange(status, row.locationId)"
                            />
                        </div>
                    </ExternalEventFirstColumnCell>
                </template>

                <template #days-rows>
                    <LayoutRow v-bind="referenceAllocationsLayoutRow">
                        <Allocation
                            v-for="allocation in referenceAllocations"
                            v-bind="allocation"
                            :key="allocation.id"
                            :allocation-usages="usages"
                        />
                    </LayoutRow>
                    <LayoutRow
                        v-for="row in layoutRows"
                        v-bind="row"
                        :key="row.rowId"
                        @create-allocation="(payload) => onLayoutRowCreate(row.locationId, payload)"
                    >
                        <Allocation
                            v-for="allocation in row.allocations"
                            v-bind="allocation"
                            :key="allocation.id"
                            :allocation-usages="usages"
                        />
                    </LayoutRow>
                </template>
            </Timeline>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useCreateAllocation } from '@/allocation/composables/useCreateAllocation';
import { useDeleteAllocation } from '@/allocation/composables/useDeleteAllocation';
import { useUpdateAllocations } from '@/allocation/composables/useUpdateAllocations';
import { useVariantSelectionPanel } from '@/components/AllocationVariantSelectionPanel/composables/useVariantSelectionPanel';
import { DEFAULT_HIGHLIGHT_VALUE } from '@/components/AllocationVariantSelectionPanel/types';
import ExternalEventsTriState from '@/components/ExternalEventsTriState/ExternalEventsTriState.vue';
import Message from '@/components/Message/Message.vue';
import Allocation from '@/components/Timeline/allocation/Allocation.vue';
import { AllocationPhaseInput } from '@/components/Timeline/allocation/types';
import ExternalEventFirstColumnCell from '@/components/Timeline/components/ExternalEventFirstColumnCell.vue';
import LayoutRow from '@/components/Timeline/components/LayoutRow.vue';
import ReferenceAllocationFirstColumnCell from '@/components/Timeline/components/ReferenceAllocationFirstColumnCell.vue';
import { useConflictMarkers } from '@/components/Timeline/composables/useConflictMarkers';
import { useTimelineConflicts } from '@/components/Timeline/composables/useTimelineConflicts';
import { useTimelineRange } from '@/components/Timeline/composables/useTimelineRange';
import { useTimelineValidation } from '@/components/Timeline/composables/useTimelineValidation';
import { countConflicts } from '@/components/Timeline/logic/conflictCounts';
import { useTimelineStore } from '@/components/Timeline/Timeline.store';
import { LocationTypeCategory } from '@/components/Timeline/Timeline.types';
import Timeline from '@/components/Timeline/Timeline.vue';
import {
    MAXIMAL_YEAR,
    MINIMAL_YEAR,
    useTimelineIntervalChanger,
} from '@/components/TimelineIntervalChanger/composables/useTimelineIntervalChanger';
import TimelineIntervalChanger from '@/components/TimelineIntervalChanger/TimelineIntervalChanger.vue';
import { useExternalEventAllocations } from '@/external_event/composables/useExternalEventAllocations';
import { useExternalEventEventTimelineData } from '@/external_event/composables/useExternalEventEventTimelineData';
import { useExternalEventInjection } from '@/external_event/composables/useExternalEventInjection';
import { useExternalEventVariantSelectionDropdown } from '@/external_event/composables/useExternalEventVariantSelectionDropdown';
import { getCategoryColor } from '@/external_event/externalEvent';
import { DeleteExternalEventAllocationDocument } from '@/generated/graphql';
import { cleanHighlight, cleanVisible } from '@/navigation/utils';
import { $t } from '@/plugins/fluent';
import { insideRangeCheck } from '@/utils/date';
import VariantSelectionDropdown from '@/variant/components/VariantSelectionDropdown.vue';
import { useHeadSafe } from '@unhead/vue';
import { useRouteQuery } from '@vueuse/router';
import { DateTime, QuarterNumbers } from 'luxon';
import { computed } from 'vue';

const props = defineProps<{ eventId: string }>();
const externalEventId = computed(() => props.eventId);
const allocationType = 'ExternalEvent';

const visible = useRouteQuery<string>('visible', '');
const highlight = useRouteQuery<string>('highlight', DEFAULT_HIGHLIGHT_VALUE);
const quarter = useRouteQuery<string>('quarter', '');
const year = useRouteQuery<string>('year', '');

const timelineStore = useTimelineStore();

const {
    startDateTime,
    endDateTime,
    isTimeSpanModified,
    previousMonthButton,
    nextMonthButton,
    dateTimeFromRoute,
    onExpandToPreviousMonth,
    onExpandToNextMonth,
    addToStartDateTime,
    subtractFromStartDateTime,
    setSelectedStartDateTime,
} = useTimelineRange(quarter, year);

const {
    variantsRef,
    setVariantsRef,
    timelineLoading,
    error: timelineError,
    onResult: onTimelineResult,
    event,
    locations,
    timelineHeaderName,
    timelineHeaderLinkTarget,
    defaultPhaseTypeId,
    executionPhasePosition,
    usages,
    setLocations,
    updateReferenceAllocationId,
    createAllocation,
    updateAllocationData,
    duplicateAllocation,
    duplicateAndUpdateAllocation,
} = useExternalEventEventTimelineData(
    externalEventId,
    startDateTime,
    endDateTime,
    dateTimeFromRoute,
    setSelectedStartDateTime,
);

const categoryColor = computed(() => getCategoryColor(event.value?.series.category));

const {
    dropDownStatus,
    visibleVariantIds,
    defaultActiveVariants,
    editableVariant,
    highlightedVariantId,
    highlightedVariant,
    mappedCurrentVariant,
    selectionPanelVariants,
    setVisibleVariantsByIds,
    setVisibleVariantsByIndexes,
    setHighlightedVariantIdById,
    setHighlightedVariantIdByIndex,
    onDeleteFilter,
    updateVisibility,
} = useVariantSelectionPanel(variantsRef, setVariantsRef, allocationType, externalEventId, visible, highlight);

const { newVariantIndex, onCreateNewVariant, onCopyAllocations } = useExternalEventVariantSelectionDropdown(
    externalEventId,
    variantsRef,
    startDateTime,
    endDateTime,
    executionPhasePosition,
    setVariantsRef,
    updateVisibility,
);

const totalAllocationCounts = computed(() => timelineStore.getAllocationTotalCounts);

const {
    filteredAllocations,
    referenceAllocationFirstColumnCell,
    referenceAllocationsLayoutRow,
    referenceAllocations,
    firstColumnCells,
    layoutRows,
    onLayoutRowCreateAllocation,
    handleStatusChange,
} = useExternalEventAllocations(
    variantsRef,
    visibleVariantIds,
    highlightedVariantId,
    externalEventId,
    locations,
    setLocations,
    totalAllocationCounts,
);

useTimelineValidation(locations, filteredAllocations);

const { conflictsByDay } = useTimelineConflicts(filteredAllocations, startDateTime, endDateTime);

const conflictMarkersByLocation = useConflictMarkers(filteredAllocations, startDateTime, endDateTime);

// Site-level conflict total for the header cell: the sum of every location's conflict spans.
// Resolved conflicts don't exist yet, so everything counts as unresolved for now.
const siteConflictsCount = computed(() => {
    let total = 0;
    for (const markers of conflictMarkersByLocation.value.values()) {
        total += countConflicts(markers);
    }
    return { resolved: 0, unresolved: total, total };
});

const visibleAllocationsCount = computed(() => {
    const start = startDateTime.value;
    const end = endDateTime.value;
    let allocationsInRange = filteredAllocations.value;
    if (start && end) {
        allocationsInRange = allocationsInRange.filter((allocation) =>
            insideRangeCheck(allocation.startDateTime, allocation.endDateTime, start, end),
        );
    }
    return allocationsInRange.length;
});

onTimelineResult((result) => {
    if (result.data) {
        if (visible.value) {
            setVisibleVariantsByIndexes(cleanVisible(visible.value));
        } else {
            setVisibleVariantsByIds(defaultActiveVariants.value.map((v) => v.id));
        }
        if (highlight.value && highlight.value !== DEFAULT_HIGHLIGHT_VALUE) {
            setHighlightedVariantIdByIndex(cleanHighlight(highlight.value));
        }
    }
});

const { onCreateAllocation } = useCreateAllocation(createAllocation, editableVariant);
const { onDeleteAllocation } = useDeleteAllocation(variantsRef, setVariantsRef);
const { updateAllocation } = useUpdateAllocations(
    updateAllocationData,
    duplicateAllocation,
    duplicateAndUpdateAllocation,
);

const { refetchHasAllocations } = useExternalEventInjection();

function onLayoutRowCreate(locationId: string, payload: { startDateTime: DateTime; endDateTime: DateTime }) {
    const editableVariantId = editableVariant.value?.id;
    // create allocation only if editableVariant is selected
    if (editableVariantId) {
        onLayoutRowCreateAllocation(locationId, payload, async (locId, start, end, onDone) => {
            await handleCreateAllocation(
                locId,
                start,
                end,
                editableVariantId,
                [
                    {
                        offset: { days: 0 },
                        duration: { days: 1 },
                        usageId: defaultPhaseTypeId.value,
                    },
                ],
                'ExternalLocation',
                onDone,
            );
        });
    }
}

async function handleDeleteAllocation(
    allocationId: string,
    _locationTypeCategory: LocationTypeCategory,
    onDone: (isSuccess: boolean) => void,
) {
    await onDeleteAllocation(allocationId, DeleteExternalEventAllocationDocument, onDone);
    refetchHasAllocations();
}

async function handleCreateAllocation(
    locationId: string,
    startDateTime: DateTime,
    endDateTime: DateTime,
    allocationVariantId: string,
    phases: AllocationPhaseInput[],
    locationTypeCategory: LocationTypeCategory,
    onDone: (isSuccess: boolean) => void,
) {
    let isFirstAllocation = false;
    const variant = variantsRef.value.find((variant) => variant.id === allocationVariantId);
    if (variant) {
        isFirstAllocation = variant.allocations.nodes.length === 0;
    }

    const response = await onCreateAllocation(
        locationId,
        startDateTime,
        endDateTime,
        allocationVariantId,
        phases,
        locationTypeCategory,
        onDone,
    );

    if (response) {
        // set first created allocation as referenceAllocation
        if (response.createdAllocationId && isFirstAllocation) {
            await updateReferenceAllocationId(allocationVariantId, response.createdAllocationId);
            timelineStore.setReferenceAllocationId(allocationVariantId, response.createdAllocationId);
        }
        refetchHasAllocations();
    }
}

const { warningNoAllocations, onChangeQuarter, onChangeYear, onClickPreviousQuarter, onClickNextQuarter } =
    useTimelineIntervalChanger(
        quarter,
        year,
        timelineLoading,
        visibleAllocationsCount,
        addToStartDateTime,
        subtractFromStartDateTime,
    );

useHeadSafe({
    title: () =>
        `${$t('allocations')} - ${event.value?.nameShort ?? ''} - ${event.value?.series?.nameShort ?? ''} - ${$t(
            'external-events',
        )}`,
});
</script>
